import { WireInstructionParams } from 'app/models/wire-instruction-params'
import { useDispatch, useSelector } from 'react-redux'
import { saveWireInstruction } from 'app/state/ducks/wire-instructions/thunks'
import {
  PaymentMethod,
  currencyOptions,
  paymentInstructionTypeOptions,
} from 'app/models/dropdown-options'
import { useEffect, useState } from 'react'
import { EntityParams } from 'app/models/entity-params'
import config from 'app/config/config'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import { Checkbox, Group, Stack, Text } from '@mantine/core'
import TextInput from 'app/views/components/inputs/TextInput'
import Select from 'app/views/components/inputs/Select'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { useForm } from '@mantine/form'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import IonIcon from '@reacticons/ionicons'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import { formatDateToUTC, stringToDate } from 'app/utils/util-functions'

type WireInstructionsFormProps = {
  closeModal: () => void
  paymentInstruction?: WireInstructionParams
  entityId?: string
  isStepper?: boolean
  onHandleSubmit?: any
  multipleWireInstructions?: WireInstructionParams[]
}

function WireInstructionsForm({
  closeModal,
  paymentInstruction,
  entityId,
  isStepper,
  onHandleSubmit,
  multipleWireInstructions,
}: WireInstructionsFormProps) {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const entities: EntityParams[] = useSelector(getEntities)
  const entity =
    entities?.find(
      e => e.id === entityId || e.id === cleanUUID(paymentInstruction?.entity.id)
    ) ?? null

  function normalizeAllPayments(arr?: string[]) {
    const vals = arr ?? [];
    // if it already contains “allPayments”, keep it
    if (vals.includes('allPayments')) {
      return ['allPayments'];
    }
    // if it has exactly both real flags, collapse into allPayments
    if (
      vals.length === 2 &&
      vals.includes('TradesWI') &&
      vals.includes('PrincipalInterestWI')
    ) {
      return ['allPayments'];
    }
    // otherwise leave as-is
    return vals;
  }

  const form = useForm({
    initialValues: {
      wireInstructions: [{
        id: paymentInstruction?.id ?? undefined,
        currency: paymentInstruction?.currency ?? '',
        entity: { admin: entity?.maker ?? '', id: entity?.id ?? '' },
        entityName: paymentInstruction?.entityName ?? '',
        paymentInstructionType: paymentInstruction?.paymentInstructionType ?? '',
        paymentReference: paymentInstruction?.paymentReference ?? '',
        specialInstructions: paymentInstruction?.specialInstructions ?? '',
        accountWithInstitutionName:
          paymentInstruction?.accountWithInstitutionName ?? undefined,
        accountWithInstitutionBankAba:
          paymentInstruction?.accountWithInstitutionBankAba ?? '',
        accountWithInstitutionSwiftBic:
          paymentInstruction?.accountWithInstitutionSwiftBic ?? '',
        accountWithInstitutionIban:
          paymentInstruction?.accountWithInstitutionIban ?? '',
        beneficiaryCustomerName:
          paymentInstruction?.beneficiaryCustomerName ?? undefined,
        beneficiaryAccountNumber:
          paymentInstruction?.beneficiaryAccountNumber ?? undefined,
        beneficiaryIban: paymentInstruction?.beneficiaryIban ?? '',
        correspondentBank:
          paymentInstruction?.paymentInstructionType !== 'Intermediary' && paymentInstruction?.paymentInstructionType !== 'CorrespondentBank'
            ? null
            : {
              bankName: paymentInstruction?.correspondentBank?.bankName ?? '',
              bankBic: paymentInstruction?.correspondentBank?.bankBic ?? '',
              bankAba: paymentInstruction?.correspondentBank?.bankAba ?? '',
            },
        intermediaryBank:
          paymentInstruction?.paymentInstructionType !== 'Intermediary'
            ? null
            : {
              bankName: paymentInstruction?.intermediaryBank?.bankName ?? '',
              bankBic: paymentInstruction?.intermediaryBank?.bankBic ?? '',
              bankAba: paymentInstruction?.intermediaryBank?.bankAba ?? '',
            },
        status: paymentInstruction?.status ?? 'Inactive',
        paymentMethod: paymentInstruction?.paymentMethod ?? undefined,
        receiverBic: paymentInstruction?.receiverBic ?? undefined,
        creditorAgent: paymentInstruction?.creditorAgent ?? undefined,
        creditorAgentBic: paymentInstruction?.creditorAgentBic ?? undefined,
        creditor: paymentInstruction?.creditor ?? undefined,
        creditorAccount: paymentInstruction?.creditorAccount ?? undefined,
        iban: paymentInstruction?.iban ?? undefined,
        usedFor: normalizeAllPayments(paymentInstruction?.usedFor),
        defaultFor: normalizeAllPayments(paymentInstruction?.defaultFor),
        effectiveDate: paymentInstruction?.effectiveDate ? stringToDate(paymentInstruction?.effectiveDate) : null,
        endDate: paymentInstruction?.endDate ? stringToDate(paymentInstruction?.endDate) : null,
      }]
    },
    validate: {
      wireInstructions: {
        usedFor: value => (value.length === 0 ? 'Required' : null),
        currency: value => (value === '' ? 'Required' : null),
        // entity: (value) => value && value.admin === "" && value.id === "" ? 'Required' : null,
        paymentInstructionType: value => (value === '' ? 'Required' : null),
        accountWithInstitutionBankAba: value =>
          value && value !== '' && value.length !== 9
            ? 'Required length of 9 characters'
            : null,
        accountWithInstitutionSwiftBic: value =>
          value && value !== '' && (value.length < 8 || value.length > 11)
            ? 'Required length between 8-11 characters'
            : null,
        correspondentBank: {
          bankBic: value =>
            value && value !== '' && (value.length < 8 || value.length > 11)
              ? 'Required length between 8-11 characters'
              : null,
          bankAba: value =>
            value && value !== '' && value.length !== 9
              ? 'Required length of 9 characters'
              : null,
        },
        intermediaryBank: {
          bankBic: value =>
            value && value !== '' && (value.length < 8 || value.length > 11)
              ? 'Required length between 8-11 characters'
              : null,
          bankAba: value =>
            value && value !== '' && value.length !== 9
              ? 'Required length of 9 characters'
              : null,
        },
      },
    },
    validateInputOnBlur: true,
  })

  useEffect(() => {
    if (multipleWireInstructions && multipleWireInstructions.length > 0) {
      const multiContactForm: { id: string; currency: string; entity: { admin: string; id: string }; entityName: string; paymentInstructionType: string; paymentReference: string; specialInstructions: string; accountWithInstitutionName: string | undefined; accountWithInstitutionBankAba: string; accountWithInstitutionSwiftBic: string; accountWithInstitutionIban: string; beneficiaryCustomerName: string | undefined; beneficiaryAccountNumber: string | undefined; beneficiaryIban: string; correspondentBank: { bankName: string; bankBic: string; bankAba: string } | null; intermediaryBank: { bankName: string; bankBic: string; bankAba: string } | null; status: string; paymentMethod: string | undefined; receiverBic: string | undefined; creditorAgent: string | undefined; creditorAgentBic: string | undefined; creditor: string | undefined; creditorAccount: string | undefined; iban: string | undefined, usedFor: string[], defaultFor: string[], effectiveDate: Date | null, endDate: Date | null }[] = []
      multipleWireInstructions.map((paymentInstruction) => {
        multiContactForm.push(
          {
            id: paymentInstruction?.id ?? '',
            currency: paymentInstruction?.currency ?? '',
            entity: { admin: entity?.maker ?? '', id: entity?.id ?? '' },
            entityName: paymentInstruction?.entityName ?? '',
            paymentInstructionType: paymentInstruction?.paymentInstructionType ?? '',
            paymentReference: paymentInstruction?.paymentReference ?? '',
            specialInstructions: paymentInstruction?.specialInstructions ?? '',
            accountWithInstitutionName:
              paymentInstruction?.accountWithInstitutionName ?? undefined,
            accountWithInstitutionBankAba:
              paymentInstruction?.accountWithInstitutionBankAba ?? '',
            accountWithInstitutionSwiftBic:
              paymentInstruction?.accountWithInstitutionSwiftBic ?? '',
            accountWithInstitutionIban:
              paymentInstruction?.accountWithInstitutionIban ?? '',
            beneficiaryCustomerName:
              paymentInstruction?.beneficiaryCustomerName ?? undefined,
            beneficiaryAccountNumber:
              paymentInstruction?.beneficiaryAccountNumber ?? undefined,
            beneficiaryIban: paymentInstruction?.beneficiaryIban ?? '',
            correspondentBank:
              paymentInstruction?.paymentInstructionType !== 'Intermediary' && paymentInstruction?.paymentInstructionType !== 'CorrespondentBank'
                ? null
                : {
                  bankName: paymentInstruction?.correspondentBank?.bankName ?? '',
                  bankBic: paymentInstruction?.correspondentBank?.bankBic ?? '',
                  bankAba: paymentInstruction?.correspondentBank?.bankAba ?? '',
                },
            intermediaryBank:
              paymentInstruction?.paymentInstructionType !== 'Intermediary'
                ? null
                : {
                  bankName: paymentInstruction?.intermediaryBank?.bankName ?? '',
                  bankBic: paymentInstruction?.intermediaryBank?.bankBic ?? '',
                  bankAba: paymentInstruction?.intermediaryBank?.bankAba ?? '',
                },
            status: paymentInstruction?.status ?? 'Inactive',
            paymentMethod: paymentInstruction?.paymentMethod ?? undefined,
            receiverBic: paymentInstruction?.receiverBic ?? undefined,
            creditorAgent: paymentInstruction?.creditorAgent ?? undefined,
            creditorAgentBic: paymentInstruction?.creditorAgentBic ?? undefined,
            creditor: paymentInstruction?.creditor ?? undefined,
            creditorAccount: paymentInstruction?.creditorAccount ?? undefined,
            iban: paymentInstruction?.iban ?? undefined,
            usedFor: normalizeAllPayments(paymentInstruction.usedFor),
            defaultFor: normalizeAllPayments(paymentInstruction.defaultFor),
            effectiveDate: paymentInstruction?.effectiveDate ? stringToDate(paymentInstruction?.effectiveDate) : null,
            endDate: paymentInstruction?.endDate ? stringToDate(paymentInstruction?.endDate) : null,

          }
        )
      })
      form.setValues({ wireInstructions: multiContactForm });
    }
  }, [multipleWireInstructions])

  async function onSubmit(values: any) {
    setIsLoading(true)
    const multiWireInstructionUpdate = multipleWireInstructions?.find(
      p => cleanUUID(p?.id) === values?.id
    )
    const usedForToSend = Array.isArray(values.usedFor) && values.usedFor.includes('allPayments')
      ? ['TradesWI', 'PrincipalInterestWI']
      : values.usedFor;

    const defaultForToSend = Array.isArray(values.defaultFor) && values.defaultFor.includes('allPayments')
      ? ['TradesWI', 'PrincipalInterestWI']
      : values.defaultFor;

    const wireInstruction: WireInstructionParams = {
      ...values,
      usedFor: usedForToSend,
      defaultFor: defaultForToSend,
      correspondentBank:
        values?.paymentInstructionType !== 'Intermediary' && values?.paymentInstructionType !== 'CorrespondentBank'
          ? null
          : {
            bankName: values?.correspondentBank?.bankName ?? '',
            bankBic: values?.correspondentBank?.bankBic ?? '',
            bankAba: values?.correspondentBank?.bankAba ?? '',
          },
      intermediaryBank:
        values?.paymentInstructionType !== 'Intermediary'
          ? null
          : {
            bankName: values?.intermediaryBank?.bankName ?? '',
            bankBic: values?.intermediaryBank?.bankBic ?? '',
            bankAba: values?.intermediaryBank?.bankAba ?? '',
          },
      effectiveDate: values.effectiveDate ? formatDateToUTC(new Date(values.effectiveDate)) : undefined,
      endDate: values.endDate ? formatDateToUTC(new Date(values.endDate)) : undefined,
      entityName: entity?.entityName ?? '',
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    }
    const response: any = await dispatch(saveWireInstruction(wireInstruction, paymentInstruction ?? multiWireInstructionUpdate))

    setIsLoading(false)
    if (response?.success) {
      SuccessNotification({
        title: 'Successfully Saved Wire Instructions',
      })
      form.reset()
      closeModal()
    } else {
      ErrorNotification({
        title: 'Failed to Save Wire Instruction',
        message: response?.payload ?? 'Check fields and try again'
      })
    }
  }

  const submitForm = (event: any) => {
    event.preventDefault()
    form.values.wireInstructions.map((c) => {
      onSubmit(c)
    })
    onHandleSubmit(form.values.wireInstructions[0])
  }


  const fields = form.values?.wireInstructions?.map((wireInstructions, index) => {
    form.values?.wireInstructions?.map((wireInstructions, index) => {
      // if (wireInstructions.paymentInstructionType == 'Intermediary') {
      form.values.wireInstructions[index].correspondentBank = {
        bankName: form.values.wireInstructions[index].correspondentBank?.bankName ?? '',
        bankBic: form.values.wireInstructions[index].correspondentBank?.bankBic ?? '',
        bankAba: form.values.wireInstructions[index].correspondentBank?.bankAba ?? '',
      }
      form.values.wireInstructions[index].intermediaryBank = {
        bankName: form.values.wireInstructions[index].intermediaryBank?.bankName ?? '',
        bankBic: form.values.wireInstructions[index].intermediaryBank?.bankBic ?? '',
        bankAba: form.values.wireInstructions[index].intermediaryBank?.bankAba ?? '',
      }
    })

    return (
      <div key={'wireInstructionsForm' + index} style={{ marginTop: '-15px' }}>
        <form>
          <Stack w="100%">
            <Group w="100%" noWrap spacing={20}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
                <div style={{ marginTop: '25px' }}>
                  <Select
                    w="100%"
                    label="Currency"
                    withAsterisk
                    searchable
                    data={currencyOptions}
                    {...form.getInputProps(`wireInstructions.${index}.currency`)}
                  />
                </div>
                <div style={{ marginTop: '25px' }}>
                  <Select
                    searchable
                    w="100%"
                    label="Type"
                    withAsterisk
                    data={paymentInstructionTypeOptions}
                    {...form.getInputProps(`wireInstructions.${index}.paymentInstructionType`)}
                  />
                </div>
              </div>

              <div className='select-field-checks'>
                <div className='select-field-checks-inside'>
                  <div className='select-field-checks-inside-label'>Used For <span style={{ color: '#fa5252' }}>*</span></div>
                  <div className='select-field-checks-inside-label-checks'>
                    <Checkbox.Group
                      value={form.values.wireInstructions[index].usedFor}
                      onChange={(newValues: string[]) => {
                        const oldValues = form.values.wireInstructions[index].usedFor;
                        const wasAll = oldValues.includes('allPayments');
                        const nowAll = newValues.includes('allPayments');
                        const hasTrades = newValues.includes('TradesWI');
                        const hasPI = newValues.includes('PrincipalInterestWI');

                        // 1) User explicitly clicked "All Payments"
                        if (!wasAll && nowAll) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.usedFor`,
                            ['allPayments']
                          );
                        }

                        // 2) User selected both Trades + Principal/Interest ⇒ treat as All Payments
                        if (!nowAll && hasTrades && hasPI) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.usedFor`,
                            ['allPayments']
                          );
                        }

                        // 3) If All was selected but user then checks one of the others ⇒ drop "allPayments"
                        if (wasAll && (hasTrades || hasPI)) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.usedFor`,
                            newValues.filter((v) => v !== 'allPayments')
                          );
                        }

                        // 4) Otherwise it’s just a normal toggle among the non-All options
                        form.setFieldValue(`wireInstructions.${index}.usedFor`, newValues);
                      }}
                    >
                      <Group mt="xs">
                        <Checkbox value="allPayments" label="All Payments" classNames={{ root: 'width-100' }} />
                        <Checkbox value="TradesWI" label="Trades" classNames={{ root: 'width-100' }} />
                        <Checkbox value="PrincipalInterestWI" label="Principal/Interest" classNames={{ root: 'width-100' }} />
                      </Group>
                    </Checkbox.Group>
                  </div>
                </div>
                <div className='select-field-checks-inside'>
                  <div className='select-field-checks-inside-label'>Default For</div>
                  <div className='select-field-checks-inside-label-checks'>
                    <Checkbox.Group
                      value={form.values.wireInstructions[index].defaultFor}
                      onChange={(newValues: string[]) => {
                        const oldValues = form.values.wireInstructions[index].defaultFor;
                        const wasAll = oldValues.includes('allPayments');
                        const nowAll = newValues.includes('allPayments');
                        const hasTrades = newValues.includes('TradesWI');
                        const hasPI = newValues.includes('PrincipalInterestWI');

                        // 1) User explicitly clicked "All Payments"
                        if (!wasAll && nowAll) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.defaultFor`,
                            ['allPayments']
                          );
                        }

                        // 2) User selected both Trades + Principal/Interest ⇒ treat as All Payments
                        if (!nowAll && hasTrades && hasPI) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.defaultFor`,
                            ['allPayments']
                          );
                        }

                        // 3) If All was selected but user then checks one of the others ⇒ drop "allPayments"
                        if (wasAll && (hasTrades || hasPI)) {
                          return form.setFieldValue(
                            `wireInstructions.${index}.defaultFor`,
                            newValues.filter((v) => v !== 'allPayments')
                          );
                        }

                        // 4) Otherwise it’s just a normal toggle among the non-All options
                        form.setFieldValue(`wireInstructions.${index}.defaultFor`, newValues);
                      }}
                    >
                      <Group mt="xs">
                        <Checkbox value="allPayments" label="All Payments" classNames={{ root: 'width-100' }} />
                        <Checkbox value="TradesWI" label="Trades" classNames={{ root: 'width-100' }} />
                        <Checkbox value="PrincipalInterestWI" label="Principal/Interest" classNames={{ root: 'width-100' }} />
                      </Group>
                    </Checkbox.Group>
                  </div>
                </div>
              </div>
            </Group>

            <Stack style={{ marginTop: '10px' }}>
              <Group noWrap>
                <CustomDatePicker
                  w="100%"
                  label={'Effective Date'}
                  clearable
                  date={form.values.wireInstructions[index].effectiveDate as unknown as Date}
                  setDate={(value: any) => { form.setFieldValue(`wireInstructions.${index}.effectiveDate`, value) }}
                  holidayCalendars={[]}

                />
                <CustomDatePicker
                  w="100%"
                  label={'Expiration Date'}
                  clearable
                  date={form.values.wireInstructions[index].endDate as unknown as Date}
                  setDate={(value: any) => form.setFieldValue(`wireInstructions.${index}.endDate`, value)}
                  holidayCalendars={[]}
                />
              </Group>
            </Stack>

            {wireInstructions.paymentInstructionType == 'Standard' && (
              <Stack>
                <div className='wire-instruction-subtitle-field'>
                  <Text className="wire-instruction-subtitle-text">
                    Account with Institution
                  </Text>
                </div>
                <Group noWrap>
                  <TextInput
                    w="100%"
                    label="Institution Name"
                    name="accountWithInstitutionName"
                    id="accountWithInstitutionName"
                    placeholder="Enter Institution Name"
                    {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionName`)}
                  />
                  {wireInstructions.currency == 'USD' || wireInstructions.currency == '' ? (
                    <TextInput
                      w="100%"
                      label="Bank ABA"
                      name="accountWithInstitutionBankAba"
                      id="accountWithInstitutionBankAba"
                      placeholder="Enter Bank ABA"
                      maxLength={9}
                      minLength={9}
                      {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionBankAba`)}
                    />
                  ) : null}
                  <TextInput
                    w="100%"
                    label="Swift BIC"
                    name="accountWithInstitutionSwiftBic"
                    id="accountWithInstitutionSwiftBic"
                    maxLength={11}
                    minLength={8}
                    placeholder="Enter Swift BIC"
                    {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionSwiftBic`)}
                  />
                </Group>
                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Beneficiary
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label="Customer Name"
                      name="beneficiaryCustomerName"
                      id="beneficiaryCustomerName"
                      placeholder="Enter Customer Name"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryCustomerName`)}
                    />

                    <TextInput
                      w="100%"
                      label="Account Number"
                      name="beneficiaryAccountNumber"
                      id="beneficiaryAccountNumber"
                      placeholder="Enter Account Number"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryAccountNumber`)}
                    />
                  </Group>
                  {wireInstructions.currency != 'USD' ? (
                    <TextInput
                      w="49.5%"
                      label="Account IBAN"
                      name="beneficiaryIban"
                      id="beneficiaryIban"
                      placeholder="Enter IBAN"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryIban`)}
                    />
                  ) : null}
                </Stack>
              </Stack>
            )}

            {wireInstructions.paymentInstructionType == 'Intermediary' && (
              <Stack>
                <div className='wire-instruction-subtitle-field'>
                  <Text className="wire-instruction-subtitle-text">
                    Correspondent
                  </Text>
                </div>
                <Group noWrap>
                  <TextInput
                    w="100%"
                    label="Bank Name"
                    name="correspondentBank.bankName"
                    id="correspondentBank.bankName"
                    placeholder="Enter Bank Name"
                    {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankName`)}
                  />
                  {wireInstructions.currency == 'USD' || wireInstructions.currency == '' ? (
                    <TextInput
                      w="100%"
                      label="Bank ABA"
                      name="correspondentBank.bankAba"
                      id="correspondentBank.bankAba"
                      placeholder="Enter Bank ABA"
                      maxLength={9}
                      minLength={9}
                      {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankAba`)}
                    />
                  ) : null}
                  {wireInstructions.currency != 'USD' ? (
                    <TextInput
                      w="100%"
                      label="Swift BIC"
                      name="correspondentBank.bankBic"
                      id="correspondentBank.bankBic"
                      placeholder="Enter Swift BIC"
                      maxLength={11}
                      minLength={8}
                      {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankBic`)}
                    />
                  ) : null}
                </Group>


                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Intermediary
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label="Bank Name"
                      name="intermediaryBank.bankName"
                      id="intermediaryBank.bankName"
                      placeholder="Enter Bank Name"
                      {...form.getInputProps(`wireInstructions.${index}.intermediaryBank.bankName`)}
                    />
                    {wireInstructions.currency == 'USD' ||
                      wireInstructions.currency == '' ? (
                      <TextInput
                        w="100%"
                        label="Bank ABA"
                        name="intermediaryBank.bankAba"
                        id="intermediaryBank.bankAba"
                        placeholder="Enter Bank ABA"
                        maxLength={9}
                        minLength={9}
                        {...form.getInputProps(`wireInstructions.${index}.intermediaryBank.bankAba`)}
                      />
                    ) : null}
                    {wireInstructions.currency != 'USD' ? (
                      <TextInput
                        w="100%"
                        label="Swift BIC"
                        name="intermediaryBank.bankBic"
                        id="intermediaryBank.bankBic"
                        placeholder="Enter Swift BIC"
                        maxLength={11}
                        minLength={8}
                        {...form.getInputProps(`wireInstructions.${index}.intermediaryBank.bankBic`)}
                      />
                    ) : null}
                  </Group>
                </Stack>
                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Account with Institution
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label="Account Name"
                      name="accountWithInstitutionName"
                      id="accountWithInstitutionName"
                      placeholder="Enter Account Name"
                      {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionName`)}
                    />
                    {wireInstructions.currency == 'USD' ||
                      wireInstructions.currency == '' ? (
                      <><TextInput
                        w="100%"
                        label="Bank ABA"
                        name="accountWithInstitutionBankAba"
                        id="accountWithInstitutionBankAba"
                        placeholder="Enter Bank ABA"
                        maxLength={9}
                        minLength={9}
                        {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionBankAba`)} /><TextInput
                          w="100%"
                          label="Swift BIC"
                          name="accountWithInstitutionSwiftBic"
                          id="accountWithInstitutionSwiftBic"
                          maxLength={11}
                          minLength={8}
                          placeholder="Enter Swift BIC"
                          {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionSwiftBic`)} /></>
                    ) : null}
                  </Group>
                  {wireInstructions.currency != 'USD' ? (
                    <Group noWrap>
                      <TextInput
                        w="100%"
                        label="Swift BIC"
                        name="accountWithInstitutionSwiftBic"
                        id="accountWithInstitutionSwiftBic"
                        minLength={8}
                        maxLength={11}
                        placeholder={
                          wireInstructions.accountWithInstitutionIban &&
                            wireInstructions.accountWithInstitutionIban.length > 0
                            ? 'Disabled for IBAN'
                            : 'Enter Swift BIC'
                        }
                        disabled={
                          wireInstructions.accountWithInstitutionIban &&
                            wireInstructions.accountWithInstitutionIban.length > 0
                            ? true
                            : false
                        }
                        {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionSwiftBic`)}
                      />
                      <TextInput
                        w="100%"
                        label="Account IBAN"
                        name="accountWithInstitutionIban"
                        id="accountWithInstitutionIban"
                        minLength={8}
                        maxLength={11}
                        placeholder={
                          wireInstructions.accountWithInstitutionSwiftBic &&
                            wireInstructions.accountWithInstitutionSwiftBic.length > 0
                            ? 'Disabled for BIC'
                            : 'Enter Account IBAN'
                        }
                        disabled={
                          wireInstructions.accountWithInstitutionSwiftBic &&
                            wireInstructions.accountWithInstitutionSwiftBic.length > 0
                            ? true
                            : false
                        }
                        {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionIban`)}
                      />
                    </Group>
                  ) : null}
                </Stack>
                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Beneficiary
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label=" Beneficiary Customer Name"
                      name="beneficiaryCustomerName"
                      id="beneficiaryCustomerName"
                      placeholder="Enter Beneficiary Customer Name"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryCustomerName`)}
                    />

                    <TextInput
                      w="100%"
                      label="Beneficiary Account No."
                      name="beneficiaryAccountNumber"
                      id="beneficiaryAccountNumber"
                      placeholder="Enter Beneficiary Account No."
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryAccountNumber`)}
                    />
                  </Group>
                  {wireInstructions.currency != 'USD' ? (
                    <TextInput
                      w="49.5%"
                      label="Beneficiary IBAN"
                      name="beneficiaryIban"
                      id="beneficiaryIban"
                      placeholder="Enter Beneficiary IBAN"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryIban`)}
                    />
                  ) : null}
                </Stack>
              </Stack>
            )}

            {wireInstructions.paymentInstructionType == 'ISO20022' && (
              <>
                <Group w="100%">
                  <Select
                    w="49%"
                    label="Payment Method"
                    withAsterisk
                    searchable
                    data={PaymentMethod}
                    {...form.getInputProps(`wireInstructions.${index}.paymentMethod`)}
                  />
                </Group>
                <div className='wire-instruction-subtitle-field'>
                  <Text className="wire-instruction-subtitle-text">
                    Intermediary Agent
                  </Text>
                </div>
                <Group w="100%">
                  <TextInput
                    w="49%"
                    label="Receiver BIC"
                    name="receiverBic"
                    id="receiverBic"
                    placeholder="Enter Receiver BIC"
                    {...form.getInputProps(`wireInstructions.${index}.receiverBic`)}
                  />
                  <TextInput
                    w="49%"
                    label="Creditor Agent"
                    name="creditorAgent"
                    id="creditorAgent"
                    placeholder="Enter Creditor Agent"
                    {...form.getInputProps(`wireInstructions.${index}.creditorAgent`)}
                  />
                </Group>
                <Group w="100%">
                  <TextInput
                    w="49%"
                    label="Creditor Agent BIC"
                    name="creditorAgentBic"
                    id="creditorAgentBic"
                    placeholder="Enter Creditor Agent BIC"
                    {...form.getInputProps(`wireInstructions.${index}.creditorAgentBic`)}
                  />
                </Group>
                <div className='wire-instruction-subtitle-field'>
                  <Text className="wire-instruction-subtitle-text">
                    Creditor Agent
                  </Text>
                </div>
                <Group w="100%">
                  <TextInput
                    w="49%"
                    label="Creditor"
                    name="creditor"
                    id="creditor"
                    placeholder="Enter Creditor"
                    {...form.getInputProps(`wireInstructions.${index}.creditor`)}
                  />
                  <TextInput
                    w="49%"
                    label="Creditor Account #"
                    name="creditorAccount"
                    id="creditorAccount"
                    placeholder="Enter Creditor Account #"
                    {...form.getInputProps(`wireInstructions.${index}.creditorAccount`)}
                  />
                </Group>
                <Group w="100%">
                  <TextInput
                    w="49%"
                    label="IBAN"
                    name="iban"
                    id="iban"
                    placeholder="Enter IBAN"
                    {...form.getInputProps(`wireInstructions.${index}.iban`)}
                  />
                </Group>
              </>
            )}

            {wireInstructions.paymentInstructionType == 'CorrespondentBank' && (
              <Stack>
                <div className='wire-instruction-subtitle-field'>
                  <Text className="wire-instruction-subtitle-text">
                    Correspondent
                  </Text>
                </div>
                <Group noWrap>
                  <TextInput
                    w="100%"
                    label="Bank Name"
                    name="correspondentBank.bankName"
                    id="correspondentBank.bankName"
                    placeholder="Enter Bank Name"
                    {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankName`)}
                  />
                  <TextInput
                    w="100%"
                    label="Bank ABA"
                    name="correspondentBank.bankAba"
                    id="correspondentBank.bankAba"
                    placeholder="Enter Bank ABA"
                    maxLength={9}
                    minLength={9}
                    {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankAba`)}
                  />
                  <TextInput
                    w="100%"
                    label="Swift BIC"
                    name="correspondentBank.bankBic"
                    id="correspondentBank.bankBic"
                    placeholder="Enter Swift BIC"
                    maxLength={11}
                    minLength={8}
                    {...form.getInputProps(`wireInstructions.${index}.correspondentBank.bankBic`)}
                  />
                </Group>


                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Account with Institution
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label="Account Name"
                      name="accountWithInstitutionName"
                      id="accountWithInstitutionName"
                      placeholder="Enter Account Name"
                      {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionName`)}
                    />
                    <TextInput
                      w="100%"
                      label="Bank ABA"
                      name="accountWithInstitutionBankAba"
                      id="accountWithInstitutionBankAba"
                      placeholder="Enter Bank ABA"
                      maxLength={9}
                      minLength={9}
                      {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionBankAba`)} />
                    <TextInput
                      w="100%"
                      label="Swift BIC"
                      name="accountWithInstitutionSwiftBic"
                      id="accountWithInstitutionSwiftBic"
                      maxLength={11}
                      minLength={8}
                      placeholder="Enter Swift BIC"
                      {...form.getInputProps(`wireInstructions.${index}.accountWithInstitutionSwiftBic`)} />
                  </Group>
                </Stack>
                <Stack>
                  <div className='wire-instruction-subtitle-field'>
                    <Text className="wire-instruction-subtitle-text">
                      Beneficiary
                    </Text>
                  </div>
                  <Group noWrap>
                    <TextInput
                      w="100%"
                      label="Beneficiary Customer Name"
                      name="beneficiaryCustomerName"
                      id="beneficiaryCustomerName"
                      placeholder="Enter Beneficiary Customer Name"
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryCustomerName`)}
                    />

                    <TextInput
                      w="100%"
                      label="Beneficiary Account No."
                      name="beneficiaryAccountNumber"
                      id="beneficiaryAccountNumber"
                      placeholder="Enter Beneficiary Account No."
                      {...form.getInputProps(`wireInstructions.${index}.beneficiaryAccountNumber`)}
                    />
                  </Group>
                  <TextInput
                    w="49.5%"
                    label="Beneficiary IBAN"
                    name="beneficiaryIban"
                    id="beneficiaryIban"
                    placeholder="Enter Beneficiary IBAN"
                    {...form.getInputProps(`wireInstructions.${index}.beneficiaryIban`)}
                  />
                </Stack>
              </Stack>
            )}

            <TextInput
              w="100%"
              label="Payment Reference"
              name="paymentReference"
              id="paymentReference"
              placeholder="Enter Payment Reference"
              {...form.getInputProps(`wireInstructions.${index}.paymentReference`)}
            />

            <TextInput
              label="Special Instructions"
              name="specialInstructions"
              id="specialInstructions"
              placeholder="Enter Special Instructions"
              {...form.getInputProps(`wireInstructions.${index}.specialInstructions`)}
            />

            {(!paymentInstruction && index > 0) && (
              <Stack style={{ marginTop: '10px' }}>
                <Group noWrap>
                  <Group noWrap w={'75%'}></Group>
                  <Group noWrap w={'100%'}>
                    <IonIcon
                      name="trash-outline"
                      className="click-ion-icon"
                      style={{ color: "red" }}
                      onClick={() => form.removeListItem('wireInstructions', index)}
                    />
                    <span
                      style={{
                        fontFamily: 'Plus Jakarta Sans',
                        color: '#111928',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      Remove
                    </span>
                  </Group>
                </Group>
              </Stack>
            )}
          </Stack>
        </form>
        {
          (!paymentInstruction && form.values.wireInstructions.length > 1) && (
            <Group
              noWrap
              style={{
                backgroundColor: '#D1D5DB',
                color: '#D1D5DB',
                height: '5px',
                marginTop: '15px',
                marginBottom: '15px',
              }}
            ></Group>
          )
        }
      </div >
    )
  });

  return (
    <FormWrapper
      title={`${paymentInstruction ? 'Edit Wire Instruction' : 'Add Wire Instructions'} ${paymentInstruction ? 'from' : 'to'} Entity`}
    >
      <div className="content">
        <>{fields}</>

        {(!paymentInstruction) && (
          <Stack
            style={{ marginTop: form.values.wireInstructions.length > 0 ? '15px' : '0px', }}
          >
            <Group noWrap>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <IonIcon
                  name="add-circle-outline"
                  className="click-ion-icon"
                  onClick={() =>
                    form.insertListItem('wireInstructions', {
                      id: undefined,
                      currency: '',
                      entity: { admin: entity?.maker ?? '', id: entity?.id ?? '' },
                      entityName: '',
                      paymentInstructionType: '',
                      paymentReference: '',
                      specialInstructions: '',
                      accountWithInstitutionName: undefined,
                      accountWithInstitutionBankAba: '',
                      accountWithInstitutionSwiftBic: '',
                      accountWithInstitutionIban: '',
                      beneficiaryCustomerName: undefined,
                      beneficiaryAccountNumber: undefined,
                      beneficiaryIban: '',
                      correspondentBank: null,
                      intermediaryBank: null,
                      status: 'Inactive',
                      paymentMethod: undefined,
                      receiverBic: undefined,
                      creditorAgent: undefined,
                      creditorAgentBic: undefined,
                      creditor: undefined,
                      creditorAccount: undefined,
                      iban: undefined,
                      usedFor: [],
                      defaultFor: [],
                      effectiveDate: undefined,
                      endDate: undefined,
                    })
                  }
                />
                <span
                  style={{
                    marginLeft: '8px',
                    fontFamily: 'Plus Jakarta Sans',
                    fontWeight: 'bold',
                    fontSize: '1em',
                  }}
                >
                  Add Wire Instructions
                </span>
              </div>
            </Group>
          </Stack>
        )}

        <Stack>
          <div
            style={{
              marginTop: '1rem',
              display: 'flex',
              gap: '.5rem',
              justifyContent: 'flex-end',
            }}
          >
            {isStepper ? (
              <>
                <PrimaryButton
                  className="form-button"
                  // disabled={!form.isValid()}  -> check validations
                  onClick={submitForm}
                  loading={isLoading}
                  type="submit"
                  w="100%"
                >
                  Save and Proceed
                </PrimaryButton>
              </>
            ) : (
              <>
                <PrimaryButton
                  className="form-button"
                  w="100%"
                  disabled={!form.isValid()}
                  onClick={submitForm}
                  loading={isLoading}
                  type="submit"
                  text="Save"
                />
                <PrimaryButton
                  className="form-button"
                  w="100%"
                  onClick={() => form.reset()}
                  text="Clear"
                />
              </>
            )}
          </div>
        </Stack>
      </div>
    </FormWrapper>
  )
}

export default WireInstructionsForm
